import { useEffect, useMemo, useState } from 'react';
import type { AdminDryRunResponse, Hit, PatternBased, PreDetectRequest, RuleBased, RuleSet } from '@kkp-fraud/shared';
import { ApiError, api, loadSample, loadSampleIndex, sampleName, type ApiSettings, type SampleScenario } from '../api';
import { skippedPatternScopes, upsertRule } from '../branches';
import { txnFieldValue } from '../fields';
import { ms } from '../format';
import { t } from '../i18n';
import { validationLine } from '../ruleCheck';
import { ActionChip, DecisionChip } from './Chips';
// Wave 7 long windows
import { LongWindowNotes } from './LongWindowNotes';
import { fill } from './Interpolated';


/** A hit on action row n of a rule with rows (`hit.branch`) reads "RB-01 · Action n". */
function hitLabel(h: Hit): string {
  if (h.branch === undefined) return h.rule_id;
  return `${h.rule_id} · ${t('branch.action.a11y', { n: h.branch })}`;
}
/**
 * The dry run inside the rule editor: the rule as it is on screen right now, dropped into
 * the draft it came from, and posted to `POST /admin/rules/dry-run`. Nothing is written —
 * not the draft (the rule is not applied), not the engine's state.
 *
 * The rule is posted as it is, rows and all: the engine evaluates action rows natively
 * (first match wins, every measure of a row counted) and names the row that fired in
 * `hit.branch` (docs/engine-rows-semantics.md).
 *
 * Two columns: the transaction on the left, the result on the right. Deliberately a copy of the few lines of
 * `DryRunTab` it needs rather than a refactor of it.
 */

export interface InlineDryRunProps {
  settings: ApiSettings;
  /** The current draft; `null` means there is nothing to run the rule against. */
  ruleSet: RuleSet | null;
  kind: 'ruleBased' | 'patternBased';
  /** The rule exactly as edited — not yet applied to the draft. */
  rule: RuleBased | PatternBased;
}

type Verdict =
  | { kind: 'matched'; rows: { branch: number; action: RuleBased['action'] }[] }
  | { kind: 'off' }
  | { kind: 'skipped'; fields: string[] }
  | { kind: 'none' };

function verdictFor(rule: RuleBased | PatternBased, result: AdminDryRunResponse, setId?: string): Verdict {
  // At most one hit per rule: the engine stops at the first matching row. A plain rule's hit
  // has no `branch` — it is row 1. 2c rule sets: another set's rule of the same id is not this one.
  const rows = result.hits
    .filter((h) => h.rule_id === rule.id && (!h.rule_set_id || !setId || h.rule_set_id === setId))
    .map((h) => ({ branch: h.branch ?? 1, action: h.action }));
  if (rows.length > 0) return { kind: 'matched', rows };
  if (!rule.enabled) return { kind: 'off' };

  // The same check `DryRunTab.analyseCoverage` makes: a required input that is null means
  // the engine skipped the rule rather than evaluated it.
  const txn = result.normalized;
  const missing: string[] = [];
  if (txn) {
    if ('clauses' in rule) {
      for (const f of rule.requires ?? []) {
        // Wave 6: a required custom field is read off `normalized.custom`.
        const v = txnFieldValue(txn, f);
        if (v === null || v === undefined) missing.push(String(f));
      }
    } else {
      missing.push(...skippedPatternScopes(rule, txn));
    }
  }
  if (missing.length > 0) return { kind: 'skipped', fields: missing };
  return { kind: 'none' };
}

export function InlineDryRun({ settings, ruleSet, kind, rule }: InlineDryRunProps) {
  const [scenarios, setScenarios] = useState<SampleScenario[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [index, setIndex] = useState(0);
  const [payload, setPayload] = useState<string>('');
  const [result, setResult] = useState<AdminDryRunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadSampleIndex()
      .then(setScenarios)
      .catch(() => setScenarios([]));
  }, []);

  const pick = async (file: string, which = 0) => {
    setSelected(file);
    setIndex(which);
    setError(null);
    try {
      const requests = await loadSample(file);
      const req = requests[Math.min(which, requests.length - 1)];
      setPayload(JSON.stringify(req, null, 2));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const run = async () => {
    if (!ruleSet) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const req = JSON.parse(payload) as PreDetectRequest;
      const res = await api.dryRun(settings, req, upsertRule(ruleSet, kind, rule));
      setResult(res);
    } catch (e) {
      if (e instanceof ApiError && e.validation) {
        setError(`${e.message}: ${validationLine(e.validation)}`);
      } else if (e instanceof SyntaxError) {
        setError(t('dryrun.invalidJson.error', { message: e.message }));
      } else {
        setError((e as Error).message);
      }
    } finally {
      setRunning(false);
    }
  };

  const verdict = useMemo(() => (result ? verdictFor(rule, result, ruleSet?.ruleSetId) : null), [rule, result, ruleSet?.ruleSetId]);
  const scenario = scenarios.find((s) => s.file === selected);

  return (
    <section className="card idr" data-testid="inline-dryrun">
      <header className="card-head">
        <h2>{t('editor.dryrun.title')}</h2>
        <span className="spacer" />
        <button type="button" className="btn primary" onClick={() => void run()} disabled={running || !payload.trim() || !ruleSet}>
          {running ? t('dryrun.running.label') : t('dryrun.run.action')}
        </button>
      </header>

      <div className="card-body idr-grid">
        {/* ---- left: the transaction ------------------------------------ */}
        <div className="idr-col stack" data-testid="inline-dryrun-input">
          {!ruleSet && <div className="note">{t('editor.dryrun.noDraft.note')}</div>}

          <div className="row-wrap">
            <span className="small muted">{t('dryrun.samples.label')}</span>
            {scenarios.length === 0 && <span className="small muted">{t('dryrun.samples.empty', { type: 'PreDetectRequest' })}</span>}
            {scenarios.map((s) => (
              <button
                key={s.file}
                type="button"
                className={`btn small${selected === s.file ? ' primary' : ''}`}
                onClick={() => void pick(s.file, 0)}
                title={s.expected}
              >
                {sampleName(s)}
                {s.requests > 1 ? ` (${s.requests})` : ''}
              </button>
            ))}
          </div>

          {scenario && scenario.requests > 1 && (
            <div className="row-wrap">
              <span className="small muted">{t('dryrun.request.label')}</span>
              {scenario.transactionIds.map((id, i) => (
                <button key={id} type="button" className={`btn small${index === i ? ' primary' : ''}`} onClick={() => void pick(scenario.file, i)}>
                  {i + 1}. {id}
                </button>
              ))}
            </div>
          )}

          <label className="field idr-json">
            PreDetectRequest JSON
            <textarea
              aria-label="PreDetectRequest JSON"
              className="mono"
              value={payload}
              placeholder={t('dryrun.request.placeholder')}
              onChange={(e) => setPayload(e.target.value)}
            />
          </label>
        </div>

        {/* ---- right: the result ---------------------------------------- */}
        <div className="idr-col stack" data-testid="inline-dryrun-result">
          {error && (
            <div className="banner error" role="alert">
              {error}
            </div>
          )}

          {!result && !error && <div className="empty small">{t('editor.dryrun.empty.label')}</div>}

          {result && verdict && (
            <>
              <div className="row-wrap">
                <DecisionChip decision={result.decision} big />
                <div className="tiles" style={{ flex: 1 }}>
                  <div className="tile">
                    <div className="label">{t('dryrun.tile.responseCode')}</div>
                    <div className="value">{result.response_code_hint}</div>
                  </div>
                  <div className="tile">
                    <div className="label">{t('dryrun.tile.latency')}</div>
                    <div className="value" style={{ fontSize: 15 }}>
                      {ms(result.engine_latency_ms)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="stack" data-testid="inline-dryrun-rule" style={{ gap: 4 }}>
                <span className="note" style={{ margin: 0 }}>
                  {t('editor.dryrun.thisRule.label')}
                </span>
                {verdict.kind === 'matched' &&
                  verdict.rows.map((row) => (
                    <div key={row.branch} className="row-wrap" data-testid={`inline-dryrun-match-${row.branch}`}>
                      <span>
                        {fill(t('editor.dryrun.matched.label', { n: row.branch }), {
                          action: <ActionChip action={row.action} />,
                        })}
                      </span>
                    </div>
                  ))}
                {verdict.kind === 'off' && <span className="chip mute">{t('state.disabled.label')}</span>}
                {verdict.kind === 'skipped' && (
                  <div className="banner warn small">{t('editor.dryrun.skipped.label', { fields: verdict.fields.join(', ') })}</div>
                )}
                {verdict.kind === 'none' && <span className="muted">{t('editor.dryrun.none.label')}</span>}
              </div>

              {/* Wave 7 — this rule's long windows: where each starts counting. */}
              <LongWindowNotes
                testid="inline-dryrun-long-windows"
                notes={result.longWindows?.filter((n) => n.ruleId === rule.id && (!ruleSet?.ruleSetId || n.ruleSetId === ruleSet.ruleSetId))}
              />

              {/* Wave 6 — what the rules read from the set's custom fields on this transaction. */}
              {result.normalized?.custom && Object.keys(result.normalized.custom).length > 0 && (
                <div className="stack" data-testid="inline-dryrun-custom" style={{ gap: 4 }}>
                  <span className="note" style={{ margin: 0 }}>
                    {t('dryrun.custom.label')}
                  </span>
                  <div className="kv">
                    {Object.entries(result.normalized.custom).map(([k, v]) => (
                      <div className="kv-row" key={k} data-testid={`inline-dryrun-custom-${k}`}>
                        <span className="k">{k}</span>
                        <span className={`v${v === null ? ' null' : ''}`}>{v === null ? 'null' : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>{t('col.rule.label')}</th>
                      <th style={{ width: 128 }}>{t('col.action.label')}</th>
                      <th>{t('col.name.label')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.hits.length === 0 && (
                      <tr>
                        <td colSpan={3} className="empty">
                          {t('dryrun.hits.empty')}
                        </td>
                      </tr>
                    )}
                    {result.hits.map((h) => (
                      // 2c rule sets: keyed with the set — two sets may both fire an RB-01
                      <tr key={`${h.rule_set_id ?? ''}/${h.rule_id}`} data-testid={`inline-hit-${h.rule_set_id ? `${h.rule_set_id}/` : ''}${h.rule_id}`}>
                        <td className="mono nowrap">{h.rule_set_id ? `${h.rule_set_id} · ${hitLabel(h)}` : hitLabel(h)}</td>
                        <td>
                          <ActionChip action={h.action} />
                        </td>
                        <td>{h.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
